import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useAppDispatch } from 'app/hooks';
import Icon from 'components/icons/Icon';
import { IconSymbol } from 'components/icons/Icon.types';
import Error404 from 'components/navigation/Error404';
import { IProject } from 'features/project/projectAPI';
import {
  deleteProjectAsync,
  getProjectAnchorId,
  patchProjectAsync,
} from 'features/project/projectSlice';
import { useUser } from 'selectors/userSelectors';

import classNames from 'classnames';
import FileUpload from 'components/files/FileUpload';
import {
  doFormMsg,
  FormMessage,
  FormMsg,
  NoFormMsg,
} from 'components/forms/FormMessage';
import {
  MarkdownContent,
  MarkdownExplainer,
} from 'components/markdown/MarkdownContent';
import { useConfirmDelete } from 'components/modal/forms/ConfirmDeleteForm';
import LiveContentWarning from 'components/page/LiveContentWarning';
import { IFile } from 'features/files/fileAPI';
import { imageFileTypes } from 'features/files/fileSlice';
import {
  getPublicationAnchorId,
  getPublicationCitation,
  sortPublicationsByDate,
} from 'features/publication/publicationSlice';
import { canEdit } from 'features/user/userSlice';
import { toDateInputValue } from 'helpers/civictech';
import { PROJECT_STATUSES } from 'helpers/consts';
import { sortBy } from 'helpers/sorting';
import { Form } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSite } from 'selectors/siteSelectors';
import { useUsers } from 'selectors/usersSelectors';
import '../static/StaticPage.scss';
import './../page/Pages.scss';

type ProjectProps = {
  project: IProject;
  className?: string;
};

export default function Project(props: ProjectProps) {
  const { project, className } = props;

  const { locale, user, userIsManager } = useUser();
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const { publications } = useSite();
  const { users } = useUsers();
  const [file, setFile] = useState<File | undefined>(undefined);
  const [saved, setSaved] = useState<boolean>(false);
  const [previewSrc, setPreviewSrc] = useState<string | null>(
    project.image?.uri ?? null,
  );

  const [editing, setEditing] = useState<boolean>(false);
  const [notfound, setNotfound] = useState<boolean>(false);
  const confirmDelete = useConfirmDelete();
  const [formMsg, setFormMsg] = useState<FormMsg>(NoFormMsg);
  const [titleEnHasContent, setTitleEnHasContent] = useState<boolean>(
    Boolean(project.title_en),
  );
  const [titleNlHasContent, setTitleNlHasContent] = useState<boolean>(
    Boolean(project.title_nl),
  );
  const formMessageContainerRef = useRef<HTMLDivElement | null>(null);
  const anchorId = getProjectAnchorId(project, true, locale);
  const [randomSide] = useState<'left' | 'right'>(
    Math.random() > 0.5 ? 'left' : 'right',
  );
  const editable = useMemo(() => canEdit(user, project), [user, project]);
  const unselectedPublications = useMemo(
    () =>
      sortPublicationsByDate(
        publications.filter(
          (pub) => !project.publications?.some((p) => p.id === pub.id),
        ),
      ),
    [publications, project.publications],
  );
  const sortedUsers = useMemo(() => {
    const u = sortBy(users, 'username');
    return u;
  }, [users]);

  useEffect(() => {
    if (editable && !project.title_en && !project.title_nl) {
      setEditing(true);
    }
  }, [editable, project.title_en, project.title_nl]);

  useEffect(() => {
    setTitleEnHasContent(Boolean(project.title_en));
  }, [project.title_en]);
  useEffect(() => {
    setTitleNlHasContent(Boolean(project.title_nl));
  }, [project.title_nl]);

  useEffect(() => {
    if (
      formMsg.message !==
      intl.formatMessage({
        id: 'GENERAL.UNSAVED_IMG',
      })
    ) {
      return;
    }
    formMessageContainerRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, [formMsg.message, intl]);

  const updateTitle_en = useCallback(
    (e: React.FocusEvent<HTMLHeadingElement>) => {
      console.log('project:', project);
      const target = e.currentTarget;
      dispatch(
        patchProjectAsync({
          data: {
            ...project,
            title_en: target.textContent ?? '',
          } as IProject,
          file: file,
          project: project,
        }),
      );
    },
    [dispatch, project, file],
  );

  const handleTitleEnInput = useCallback(
    (e: React.InputEvent<HTMLHeadingElement>) => {
      setTitleEnHasContent((e.currentTarget.textContent ?? '').length > 0);
    },
    [],
  );
  const handleTitleNlInput = useCallback(
    (e: React.InputEvent<HTMLHeadingElement>) => {
      setTitleNlHasContent((e.currentTarget.textContent ?? '').length > 0);
    },
    [],
  );

  const handleAuthorChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedAuthor = users.find(
        (user) => user.id === Number(e.currentTarget.value),
      );
      if (!selectedAuthor) return;
      dispatch(
        patchProjectAsync({
          data: {
            ...project,
            author: selectedAuthor,
          } as IProject,
        }),
      );
    },
    [dispatch, project, users],
  );

  const handleStatusChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      dispatch(
        patchProjectAsync({
          data: {
            ...project,
            status: e.currentTarget.value as IProject['status'],
          } as IProject,
        }),
      );
    },
    [dispatch, project],
  );

  const updateTitle_nl = useCallback(
    (e: React.FocusEvent<HTMLHeadingElement>) => {
      const target = e.currentTarget;
      dispatch(
        patchProjectAsync({
          data: {
            ...project,
            title_nl: target.textContent ?? '',
          } as IProject,
        }),
      );
    },
    [dispatch, project],
  );

  const updateText_en = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      dispatch(
        patchProjectAsync({
          data: {
            ...project,
            text_en: e.currentTarget.value,
          } as IProject,
        }),
      );
    },
    [dispatch, project],
  );

  const updateText_nl = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      dispatch(
        patchProjectAsync({
          data: {
            ...project,
            text_nl: e.currentTarget.value,
          } as IProject,
        }),
      );
    },
    [dispatch, project],
  );

  const updateStartDate = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      dispatch(
        patchProjectAsync({
          data: {
            ...project,
            startDate: new Date(e.currentTarget.value),
          } as IProject,
        }),
      );
    },
    [dispatch, project],
  );

  const updateEndDate = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const d = e.currentTarget.value?.length
        ? new Date(e.currentTarget.value)
        : null;
      dispatch(
        patchProjectAsync({
          data: {
            ...project,
            endDate: d,
          } as IProject,
        }),
      );
    },
    [dispatch, project],
  );

  const updateDuration = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      dispatch(
        patchProjectAsync({
          data: {
            ...project,
            duration: e.currentTarget.value,
          } as IProject,
        }),
      );
    },
    [dispatch, project],
  );

  const handleRemoveProject = useCallback(() => {
    function doDelete(): boolean {
      dispatch(
        deleteProjectAsync({
          id: project.id!,
        }),
      ).then((response: any) => {
        switch (response.payload?.response.status) {
          case 204:
            break;
          case 500:
          default:
            console.log(response);
        }
      });
      return true;
    }
    confirmDelete({
      prompts: {
        prompt1: (
          <FormattedMessage
            id="PROJECTS.DELETE_CONFIRM"
            values={{ project: project.title_en }}
          />
        ),
        prompt2: <FormattedMessage id="PROJECTS.DELETE_CONFIRM_2" />,
      },
      again: { serious: true },
    }).then((confirmed: boolean) => {
      if (confirmed) doDelete();
    });
  }, [dispatch, project, confirmDelete]);

  const handleImageSelect = useCallback<
    Dispatch<SetStateAction<File | undefined>>
  >((file) => {
    setFile(file);
    if (file) {
      setSaved(false); // mark as not saved when a new file is selected
    } else {
      setSaved(true);
    }
  }, []);

  const handleImageUpload = useCallback(() => {
    dispatch(
      patchProjectAsync({
        data: {
          ...project,
        } as IProject,
        file: file,
        project: project,
      }),
    ).then((response: any) => {
      switch (response.payload?.response.status) {
        case 200:
          setSaved(true);
          doFormMsg(
            {
              message: intl.formatMessage({
                id: 'GENERAL.IMAGE_UPLOAD_SUCCESS',
              }),
              success: true,
              timeout: 5000,
            },
            setFormMsg,
          );
          break;
        case 500:
        default:
          doFormMsg(
            {
              message: intl.formatMessage({
                id: 'GENERAL.IMAGE_UPLOAD_FAILED',
              }),
              success: false,
            },
            setFormMsg,
          );
          break;
      }
    });
  }, [dispatch, project, file]);

  const handleDeleteImage = useCallback(() => {
    dispatch(
      patchProjectAsync({
        data: {
          ...project,
          image: null,
        } as IProject,
        project: project,
      }),
    );
  }, [dispatch, project]);

  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, checked } = event.target;
      dispatch(
        patchProjectAsync({
          data: {
            ...project,
            [name]: checked,
          } as IProject,
          project: project,
        }),
      );
    },
    [dispatch, project],
  );

  const handleToggleEditing = useCallback(() => {
    if (editing && saved !== true && file) {
      doFormMsg(
        {
          message: intl.formatMessage({
            id: 'GENERAL.UNSAVED_IMG',
          }),
          success: false,
          timeout: 5000,
        },
        setFormMsg,
      );
      return;
    }
    setEditing((currentEditing) => !currentEditing);
  }, [editing, intl, saved, file]);

  const handleAddPublication = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedId = Number(e.currentTarget.value);
      const publicationToAdd = publications.find(
        (pub) => pub.id === selectedId,
      );
      if (publicationToAdd) {
        dispatch(
          patchProjectAsync({
            data: {
              ...project,
              publications: [...(project.publications || []), publicationToAdd],
            } as IProject,
          }),
        );
        e.currentTarget.value = '';
      }
    },
    [dispatch, project, publications],
  );

  const handleRemovePublication = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedId = Number(e.currentTarget.value);
      dispatch(
        patchProjectAsync({
          data: {
            ...project,
            publications: (project.publications || []).filter(
              (p) => p.id !== selectedId,
            ),
          } as IProject,
        }),
      );
      e.currentTarget.value = '';
    },
    [dispatch, project],
  );
  const isEditing = editable && editing;

  return (
    <div className="item_container">
      <div
        className={classNames(`project ${className ?? ''}`, {
          editing: isEditing,
        })}
      >
        {notfound || !project ? (
          <Error404 />
        ) : (
          <>
            {isEditing ? (
              <div id={anchorId}>
                <LiveContentWarning editing={editing} />
                {userIsManager ? (
                  <select
                    name="project_author"
                    className="page_author_select"
                    value={project.author?.id ?? ''}
                    onChange={handleAuthorChange}
                  >
                    <option value="" disabled>
                      {intl.formatMessage({ id: 'GENERAL.AUTHOR' })}
                    </option>
                    {sortedUsers.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.username}
                      </option>
                    ))}
                  </select>
                ) : null}
                <div className="settings_label">
                  <FormattedMessage id="GENERAL.LOCALE.EN" />
                </div>
                <section className="page_header">
                  <h2
                    className={classNames('page_title', {
                      plch: !titleEnHasContent,
                    })}
                    contentEditable
                    suppressContentEditableWarning
                    data-placeholder={intl.formatMessage({
                      id: 'PROJECTS.TITLE_EN_PLCH',
                    })}
                    onInput={handleTitleEnInput}
                    onBlur={updateTitle_en}
                  >
                    {project.title_en ?? ''}
                  </h2>
                </section>
                <section className="page_body">
                  <textarea
                    className="page_text"
                    defaultValue={project.text_en}
                    placeholder={intl.formatMessage({
                      id: 'PROJECTS.TEXT_EN_PLCH',
                    })}
                    onBlur={updateText_en}
                  />
                </section>
                <MarkdownExplainer />
                <div className="settings_label">
                  <FormattedMessage id="GENERAL.LOCALE.NL" />
                </div>
                <section className="page_header">
                  <h2
                    className={classNames('page_title', {
                      plch: !titleNlHasContent,
                    })}
                    contentEditable
                    suppressContentEditableWarning
                    data-placeholder={intl.formatMessage({
                      id: 'PROJECTS.TITLE_NL_PLCH',
                    })}
                    onInput={handleTitleNlInput}
                    onBlur={updateTitle_nl}
                  >
                    {project.title_nl ?? ''}
                  </h2>
                </section>
                <section className="page_body">
                  <textarea
                    className="page_text"
                    defaultValue={project.text_nl}
                    placeholder={intl.formatMessage({
                      id: 'PROJECTS.TEXT_NL_PLCH',
                    })}
                    onBlur={updateText_nl}
                  />
                </section>
                <div className="settings_label">
                  <FormattedMessage id="GENERAL.STATUS.STATUS" />
                </div>
                <div className="item_dates">
                  <div className="settings_label inline">
                    <FormattedMessage id="GENERAL.DATES.START" />
                  </div>
                  <input
                    className="page_text"
                    type="date"
                    defaultValue={toDateInputValue(project.startDate)}
                    onBlur={updateStartDate}
                  />
                  <div className="settings_label inline">
                    <FormattedMessage id="GENERAL.DATES.END" />
                  </div>
                  <input
                    className="page_text"
                    type="date"
                    defaultValue={toDateInputValue(project.endDate)}
                    onBlur={updateEndDate}
                  />
                  <div className="settings_label inline">
                    <FormattedMessage id="GENERAL.DATES.DURATION" />
                  </div>
                  <input
                    className="page_text"
                    type="text"
                    defaultValue={project.duration ?? ''}
                    onBlur={updateDuration}
                  />
                  <div className="settings_label inline">
                    <FormattedMessage id="GENERAL.STATUS.STATUS" />
                  </div>
                  <select
                    name="project_status"
                    className="page_author_select"
                    value={project.status}
                    onChange={handleStatusChange}
                  >
                    {PROJECT_STATUSES.map((status) => (
                      <option key={status} value={status}>
                        {intl.formatMessage({
                          id: `GENERAL.STATUS.${status}`.toUpperCase(),
                        })}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="settings_label">
                  <FormattedMessage id="PROJECTS.PUBLICATIONS" />
                </div>
                <div className="items_dual_select">
                  <div className="items_column">
                    <div className="settings_label inline">
                      <FormattedMessage id="PUBLICATIONS.SELECT" />
                    </div>
                    <Form.Select
                      name="available_publications"
                      className="items_select"
                      value=""
                      htmlSize={5}
                      onChange={handleAddPublication}
                      // style={{ height: '8rem' }}
                    >
                      <option value="">-- Select to add --</option>
                      {unselectedPublications.map((publication) => (
                        <option
                          key={publication.id}
                          value={publication.id}
                          title={`${new Date(
                            publication.publicationDate!,
                          ).getFullYear()} ${publication.title} (id: ${publication.id})`}
                        >
                          {new Date(publication.publicationDate!).getFullYear()}{' '}
                          {publication.title} (id: {publication.id})
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                  <div className="items_column">
                    <div className="settings_label inline">
                      <FormattedMessage id="PROJECTS.PUBLICATIONS" />
                    </div>
                    <Form.Select
                      name="selected_publications"
                      className="items_select"
                      value=""
                      htmlSize={5}
                      onChange={handleRemovePublication}
                      // style={{ height: '8rem' }}
                    >
                      <option value="">-- Select to remove --</option>
                      {sortPublicationsByDate(project.publications || []).map(
                        (publication) => (
                          <option
                            key={publication.id}
                            value={publication.id}
                            title={`${new Date(
                              publication.publicationDate!,
                            ).getFullYear()} ${publication.title} (id: ${publication.id})`}
                          >
                            {new Date(
                              publication.publicationDate!,
                            ).getFullYear()}{' '}
                            {publication.title} (id: {publication.id})
                          </option>
                        ),
                      )}
                    </Form.Select>
                  </div>
                </div>
                <div className="settings_label">
                  <FormattedMessage id="GENERAL.IMAGE" />
                </div>
                <div
                  ref={formMessageContainerRef}
                  className="form_message_container"
                >
                  <FormMessage {...formMsg} />
                </div>
                <div className="image_settings">
                  <div
                    className={classNames('page_image editing', {
                      image_contain: !!project.image_contain,
                    })}
                  >
                    <FileUpload
                      types={imageFileTypes}
                      currentSrc={project.image?.uri || null}
                      previewSrc={previewSrc}
                      size={200}
                      defaultImg={null}
                      setPreviewSrc={setPreviewSrc}
                      showTrigger={true}
                      showMsg={(m) => doFormMsg(m, setFormMsg)}
                      setFile={handleImageSelect}
                      onDelete={handleDeleteImage}
                      onSave={handleImageUpload}
                      saved={saved}
                      confirmationText={
                        <FormattedMessage id="GENERAL.DELETE_IMAGE_CNF" />
                      }
                      disabled={!editing}
                    />
                  </div>
                  <Form.Check
                    className="image_contain"
                    id={'contain'}
                    type="checkbox"
                    label={intl.formatMessage({ id: 'GENERAL.IMAGE_CONTAIN' })}
                    name="image_contain"
                    checked={!!project.image_contain}
                    onChange={handleCheckboxChange}
                  />
                </div>
              </div>
            ) : (
              <>
                <section className="page_header" id={anchorId}>
                  {locale === 'en' ? (
                    <h2>{project.title_en}</h2>
                  ) : (
                    <h2>{project.title_nl}</h2>
                  )}
                  {user &&
                  (project.startDate || project.endDate || project.duration) ? (
                    <div className="project_dates meta_data">
                      {project.startDate ? (
                        <>
                          {/* <div className="settings_label inline">
                            <FormattedMessage id="GENERAL.DATES.START" />
                          </div> */}
                          <div>{toDateInputValue(project.startDate)}</div>
                        </>
                      ) : null}
                      {' - '}
                      {project.endDate ? (
                        <>
                          {/* <div className="settings_label inline">
                            <FormattedMessage id="GENERAL.DATES.END" />
                          </div> */}
                          <div>{toDateInputValue(project.endDate)}</div>
                        </>
                      ) : null}
                    </div>
                  ) : null}
                  {user ? (
                    <div className="project_status meta_data">
                      {/* <div className="settings_label inline">
                        <FormattedMessage id="GENERAL.STATUS.STATUS" />
                      </div> */}
                      <div>
                        {intl.formatMessage({
                          id: `GENERAL.STATUS.${project.status}`.toUpperCase(),
                        })}
                      </div>
                      {/* {project.duration ? (
                        <>
                          <div className="settings_label inline">
                            <FormattedMessage id="GENERAL.DATES.DURATION" />
                          </div>
                          <div>{project.duration}</div>
                        </>
                      ) : null} */}
                    </div>
                  ) : null}
                </section>
                <section className="page_body">
                  {project.image ? (
                    <div
                      className={classNames(`page_image ${randomSide}`, {
                        image_contain: !!project.image_contain,
                      })}
                    >
                      <img
                        src={(project.image as IFile).uri}
                        alt={
                          locale === 'en' ? project.title_en : project.title_nl
                        }
                      />
                    </div>
                  ) : null}
                  {locale === 'en' ? (
                    <MarkdownContent
                      className="page_text"
                      collapsible={true}
                      maxLines={10}
                    >
                      {project.text_en}
                    </MarkdownContent>
                  ) : (
                    <MarkdownContent
                      className="page_text"
                      collapsible={true}
                      maxLines={10}
                    >
                      {project.text_nl}
                    </MarkdownContent>
                  )}
                </section>
                <section className="page_body">
                  {project.publications && project.publications.length > 0 ? (
                    <div className="items_links">
                      <span className="label">
                        <FormattedMessage id="PROJECTS.PUBLICATIONS" />
                      </span>
                      {sortPublicationsByDate(project.publications).map(
                        (publication) => (
                          <a
                            href={
                              '/publications#' +
                              getPublicationAnchorId(publication)
                            }
                            key={publication.id}
                          >
                            <MarkdownContent key={publication.id}>
                              {getPublicationCitation(publication) ?? ''}
                            </MarkdownContent>
                          </a>
                        ),
                      )}
                    </div>
                  ) : null}
                </section>
              </>
            )}
          </>
        )}
      </div>
      {user ? (
        <div className="section_menu">
          {editable ? (
            <>
              <Icon
                symbol={editing ? IconSymbol.visible : IconSymbol.pencil}
                onClick={handleToggleEditing}
                size={20}
                hintProps={{
                  hint: editing
                    ? { id: 'PAGES.PREVIEW_MODE' }
                    : { id: 'PAGES.EDIT_MODE' },
                }}
              />
              <Icon
                inline
                className="icon_button"
                symbol={IconSymbol.bin}
                size={18}
                onClick={() => handleRemoveProject()}
                hintProps={{ hint: { id: 'PROJECTS.REMOVE_PROJECT' } }}
              />
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
